import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProcContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import {
  useProcDuplicate,
  useUpdateStep,
  useStepRemove,
  UpdateStepError,
  useCreateComponent,
  CreateComponentError,
  StepRemoveError,
  useDuplicateStep,
  ComponentResponse,
  useDeleteComponent,
  DeleteComponentError,
  ComponentAddRequest,
  useAddNewStep,
  useProcEdit,
  ProcEditError,
  AuthorProcEditRequest,
  useProcPatchDetailsEdit,
  AuthorProcPatchRequest,
  ProcPatchDetailsEditError,
  useUpdateStepLogic,
  UpdateStepLogic,
  UpdateStepLogicError,
  ProcDetailsResponse,
  StepUpdateRequest,
  useUpdateComponent,
  ComponentUpdateRequest,
  UpdateComponentError,
} from '@/shared/api';
import { useDataInvalidation } from '@/shared/hooks';
import { captureError } from '@/shared/utils';

export const useProcManageActions = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationContext();
  const { proc } = useProcContext();
  const isPublished = proc?.state === 'published';
  const invalidate = useDataInvalidation(['procs', proc?.id]);

  const { mutateAsync: createDraftProc } = useProcDuplicate();
  const { mutate: editProcMutation } = useProcEdit();
  const { mutate: patchProcMutation, isLoading: patchProcDetailsLoading } =
    useProcPatchDetailsEdit();
  const { mutate: addComponent } = useCreateComponent();
  const { mutate: deleteComponent } = useDeleteComponent();
  const { mutate: updateComponentMutation, isLoading: updateComponentLoading } =
    useUpdateComponent();
  const { mutate: addStepMutation, isLoading: addStepLoading } =
    useAddNewStep();
  const { mutate: duplicateStepMutation } = useDuplicateStep();
  const { mutate: updateStepMutation } = useUpdateStep();
  const { mutate: removeStepMutation } = useStepRemove();
  const { mutate: saveStepLogicMutation } = useUpdateStepLogic();

  const handleDraftCreation = useCallback(async () => {
    if (isPublished) {
      const newProc = await createDraftProc({
        pathParams: { id: proc.id },
        queryParams: { linkToBase: true },
      });
      return newProc.id;
    }
    return proc?.id || '';
  }, [proc?.id, createDraftProc, isPublished]);

  const onAfterProcUpdate = useCallback(
    (newProcId: string) =>
      isPublished
        ? navigate(`/dashboard/author/proc/${newProcId}`, { replace: true })
        : invalidate(),
    [invalidate, isPublished, navigate],
  );

  const editProc = useCallback(
    async (options: {
      data: AuthorProcEditRequest;
      onSuccess?: () => void;
      onError?: (error: ProcEditError) => void;
    }) => {
      const procId = await handleDraftCreation();
      editProcMutation(
        {
          pathParams: { id: procId },
          body: options.data,
        },
        {
          onSuccess() {
            onAfterProcUpdate(procId);
            options.onSuccess?.();
          },
          onError(error) {
            captureError(error, {
              module: 'proc_actions',
              action: 'edit_proc',
            });
            showNotification({
              type: 'error',
              message: 'Proc edit error',
              description:
                error.message || 'An error occurred while editing the proc',
            });
            options.onError?.(error);
          },
        },
      );
    },
    [
      editProcMutation,
      handleDraftCreation,
      onAfterProcUpdate,
      showNotification,
    ],
  );

  const patchProcDetails = useCallback(
    async (options: {
      procId: string;
      data: AuthorProcPatchRequest;
      onBeforeUpdate?: () => void;
      onSuccess?: () => void;
      onError?: (error: ProcPatchDetailsEditError) => void;
    }) => {
      patchProcMutation(
        {
          pathParams: { id: options.procId },
          body: options.data,
        },
        {
          onSuccess() {
            onAfterProcUpdate(options.procId);
            options.onSuccess?.();
          },
          onError(error) {
            captureError(error, {
              module: 'proc_actions',
              action: 'update_proc_details',
            });
            showNotification({
              type: 'error',
              message: 'Proc details update error',
              description:
                error.message ||
                'An error occurred while updating the proc details',
            });
            options.onError?.(error);
          },
        },
      );
    },
    [patchProcMutation, onAfterProcUpdate, showNotification],
  );

  const addComponentToStep = useCallback(
    async (options: {
      component: ComponentAddRequest;
      stepId: string;
      onSuccess?: (component: ComponentResponse) => void;
      onError?: (error: CreateComponentError) => void;
    }) => {
      const procId = await handleDraftCreation();
      addComponent(
        {
          pathParams: { procId, stepId: options.stepId },
          body: options.component,
        },
        {
          onSuccess(component) {
            onAfterProcUpdate(procId);
            options.onSuccess?.(component);
          },
          onError(error) {
            captureError(error, {
              module: 'proc_actions',
              action: 'add_component',
            });
            showNotification({
              type: 'error',
              message: 'Component addition error',
              description:
                error.message || 'An error occurred while adding the component',
            });
            options.onError?.(error);
          },
        },
      );
    },
    [handleDraftCreation, addComponent, onAfterProcUpdate, showNotification],
  );

  const deleteComponentFromStep = useCallback(
    async (options: {
      componentId: string;
      stepId: string;
      onSuccess?: () => void;
      onError?: (error: DeleteComponentError) => void;
    }) => {
      const procId = await handleDraftCreation();
      deleteComponent(
        {
          pathParams: {
            procId,
            stepId: options.stepId,
            componentId: options.componentId,
          },
        },
        {
          onSuccess() {
            onAfterProcUpdate(procId);
            options.onSuccess?.();
          },
          onError(error) {
            captureError(error, {
              module: 'proc_actions',
              action: 'delete_component',
            });
            showNotification({
              type: 'error',
              message: 'Component deletion error',
              description:
                error.message ||
                'An error occurred while deleting the component',
            });
            options.onError?.(error);
          },
        },
      );
    },
    [deleteComponent, handleDraftCreation, onAfterProcUpdate, showNotification],
  );

  const updateStepComponent = useCallback(
    async (options: {
      procId?: string;
      stepId: string;
      data: ComponentUpdateRequest;
      onSuccess?: () => void;
      onError?: (error: UpdateComponentError) => void;
    }) => {
      const procId = options.procId || (await handleDraftCreation());
      updateComponentMutation(
        {
          pathParams: { procId, stepId: options.stepId },
          body: options.data,
        },
        {
          onSuccess() {
            onAfterProcUpdate(procId);
            options.onSuccess?.();
          },
          onError(error) {
            captureError(error, {
              module: 'proc_actions',
              action: 'update_component',
            });
            showNotification({
              type: 'error',
              message: 'Component update error',
              description:
                error.message ||
                'An error occurred while updating the component',
            });
            options.onError?.(error);
          },
        },
      );
    },
    [
      handleDraftCreation,
      onAfterProcUpdate,
      showNotification,
      updateComponentMutation,
    ],
  );

  const addStep = useCallback(
    async (options: {
      title: string;
      onSuccess?: (res?: ProcDetailsResponse) => void;
      onError?: (error: unknown) => void;
    }) => {
      const procId = await handleDraftCreation();
      addStepMutation(
        {
          pathParams: { id: procId },
          body: { title: options.title },
        },
        {
          onSuccess(step) {
            onAfterProcUpdate(procId);
            options.onSuccess?.(step);
          },
          onError(error) {
            showNotification({
              type: 'error',
              message: 'Step add error',
              description:
                error.message || 'An error occurred while adding the step',
            });
            captureError(error, {
              module: 'sidebar_left_on_error_handle_step_add',
              action: 'handle_step_add',
            });
            options.onError?.(error);
          },
        },
      );
    },
    [addStepMutation, handleDraftCreation, onAfterProcUpdate, showNotification],
  );

  const duplicateStep = useCallback(
    async (options: {
      stepId: string;
      onSuccess?: (step: ProcDetailsResponse) => void;
      onError?: (error: UpdateStepError) => void;
    }) => {
      const procId = await handleDraftCreation();
      duplicateStepMutation(
        { pathParams: { id: procId, stepId: options.stepId } },
        {
          onSuccess(step) {
            onAfterProcUpdate(procId);
            options.onSuccess?.(step);
          },
          onError(error) {
            captureError(error, {
              module: 'proc_actions',
              action: 'duplicate_step',
            });
            showNotification({
              type: 'error',
              message: 'Step duplication error',
              description:
                error.message || 'An error occurred while duplicating the step',
            });
            options.onError?.(error);
          },
        },
      );
    },
    [
      handleDraftCreation,
      duplicateStepMutation,
      onAfterProcUpdate,
      showNotification,
    ],
  );

  const updateStep = useCallback(
    async (options: {
      stepId: string;
      data: StepUpdateRequest;
      onSuccess?: () => void;
      onError?: (error: UpdateStepError) => void;
    }) => {
      const procId = await handleDraftCreation();
      updateStepMutation(
        {
          pathParams: { id: procId, stepId: options.stepId },
          body: options.data,
        },
        {
          onSuccess() {
            onAfterProcUpdate(procId);
            options.onSuccess?.();
          },
          onError(error) {
            captureError(error, {
              module: 'proc_actions',
              action: 'update_step',
            });
            showNotification({
              type: 'error',
              message: 'Step update error',
              description:
                error.message || 'An error occurred while updating the step',
            });
            options.onError?.(error);
          },
        },
      );
    },
    [
      handleDraftCreation,
      updateStepMutation,
      onAfterProcUpdate,
      showNotification,
    ],
  );

  const removeStep = useCallback(
    async (options: {
      stepId: string;
      onSuccess?: () => void;
      onError?: (error: StepRemoveError) => void;
    }) => {
      const procId = await handleDraftCreation();
      removeStepMutation(
        { pathParams: { id: procId, stepId: options.stepId } },
        {
          onSuccess() {
            onAfterProcUpdate(procId);
            options?.onSuccess?.();
          },
          onError(error) {
            captureError(error, {
              module: 'proc_actions',
              action: 'remove_step',
            });
            showNotification({
              type: 'error',
              message: 'Step removal error',
              description:
                error.message || 'An error occurred while removing the step',
            });
            options?.onError?.(error);
          },
        },
      );
    },
    [
      handleDraftCreation,
      onAfterProcUpdate,
      removeStepMutation,
      showNotification,
    ],
  );

  const saveStepLogic = useCallback(
    async (options: {
      stepId: string;
      data: UpdateStepLogic;
      onSuccess?: () => void;
      onError?: (error: UpdateStepLogicError) => void;
    }) => {
      const procId = await handleDraftCreation();
      saveStepLogicMutation(
        {
          pathParams: { procId, stepId: options.stepId },
          body: options.data,
        },
        {
          onSuccess() {
            onAfterProcUpdate(procId);
            options.onSuccess?.();
          },
          onError(error) {
            captureError(error, {
              module: 'proc_actions',
              action: 'save_step_logic',
            });
            showNotification({
              type: 'error',
              message: 'Step logic save error',
              description:
                error.message ||
                'An error occurred while saving the step logic',
            });
            options.onError?.(error);
          },
        },
      );
    },
    [
      handleDraftCreation,
      onAfterProcUpdate,
      saveStepLogicMutation,
      showNotification,
    ],
  );

  return {
    handleDraftCreation,
    editProc,
    patchProcDetails,
    patchProcDetailsLoading,
    addComponentToStep,
    deleteComponentFromStep,
    updateStepComponent,
    updateComponentLoading,
    addStep,
    addStepLoading,
    duplicateStep,
    updateStep,
    removeStep,
    saveStepLogic,
  };
};
